/**
 * LegacyStoreShim（design.md参照）。
 *
 * Header.vue（+ GrobalNav / CartIcon / HeaderPointModal）が mapState / mapGetters
 * で参照するキーだけを満たす最小の Vuex store shim。SPA の ApplicationsStoreConfig
 * は持ち込まない。
 *
 * 呼び出し側（main.js、タスク2.3）が `Vue.use(Vuex)` 済みであることが前提。
 * このモジュール自体は Vue.use(Vuex) を呼ばない（グローバル Vue を勝手に変更しない）。
 */
import Vuex from 'vuex';

const memberModule = {
  namespaced: true,
  state: () => ({
    user: null,
  }),
  getters: {
    isLoggedIn: state => !!state.user,
    loggedUser: state => state.user,
    // real MemberStore（apps/src/models/modules/MemberStore.js）と同じ判定基準
    isAgent: state => !!(state.user && state.user.agency_id != null),
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
  },
};

/**
 * LegacyStoreShimを満たすVuex storeインスタンスを新規作成する。
 * ページごとに1個だけ生成し、全島で共有する想定（main.jsの責務）。
 */
export function createLegacyStore() {
  return new Vuex.Store({
    state: {
      // SPA（apps/src/models/ApplicationsStoreConfig.js、
      // apps/src_sp/models/ApplicationsStoreConfig.js）と同一の判定。
      // createLegacyStore()は呼び出しごとに評価される関数のため、
      // ApplicationsStoreConfigのstate()ファクトリと同様にUAの
      // 差し替え後即座に再評価できる。
      // Header.vue の v-if="!isNative"（PC:#header-index / grobal-nav、
      // SP:<header>）をレガシー島でも発火させ、アプリ内WebViewでの
      // 共通可視ヘッダー非表示を実現する（要件3.2）。
      isNative: /reactNative/.test(window.navigator.userAgent),
      // GrobalNav: mapState({ isDynamicPackage: 'isDynamicPackage' })
      isDynamicPackage: false,
      // MediaStore（apps/src_common/models/modules/MediaStore.js）の初期状態と
      // 同じ「mediaキーを持つオブジェクト」にする。裸のnull/文字列は不可。
      //
      // レガシー島では setMedia() を呼ぶ経路が無いので常に { media: null }。
      // 読み出しは getters.quotedMedia 経由に統一されており（MED-1517）、
      // この state を直接読むコンポーネントは現在ひとつも無い。
      media: { media: null },
      isFromMetaSearch: false,
      // CompleteCrossSellBanner が購読する（design.md CrossSellData）。
      // setCrossSellData() が呼ばれるまではnull（何も描画しない）。
      crossSell: null,
    },
    mutations: {
      setMedia(state, media) {
        state.media = media;
      },
      setIsFromMetaSearch(state, isFromMetaSearch) {
        state.isFromMetaSearch = isFromMetaSearch;
      },
      setCrossSellData(state, data) {
        state.crossSell = data;
      },
    },
    getters: {
      isFromMetaSearch: state => !!state.isFromMetaSearch,
      // GrobalNav用。レガシー画面では商材の出し分け対象が無いため既定はfalse。
      hasHotel: () => false,
      hasFlights: () => false,
      hasBothFlightsAndHotel: () => false,
      // HeaderPointModal が mapGetters で参照する（MED-1517）。
      // レガシー島には assets の見積もりを取る経路が無いので常に null。
      // shim に無いと Vuex が dev ビルドで
      // 「[vuex] unknown getter: quotedMedia」を毎ページ出力する。
      quotedMedia: () => null,
    },
    modules: {
      member: memberModule,
    },
  });
}

/**
 * $route.query.ab をwindow.location.searchから1度だけ解決する（design.md
 * LegacyRouteStub、要件14.7）。AppDownloadBannerのAbTreatmentMixin判定にのみ使う。
 */
function resolveAbQueryParam() {
  if (typeof window === 'undefined' || !window.location) return undefined;
  return new URLSearchParams(window.location.search).get('ab') || undefined;
}

/**
 * Header.vue が読む $route スタブの既定値。
 * main.js（タスク2.3）が data-tvl-page-name からインスタンス単位で上書きする。
 * query.ab は AppDownloadBanner マウント時の AbTreatmentMixin 判定にのみ使う（要件14.7）。
 */
export const defaultRouteStub = {
  name: '',
  query: { ab: resolveAbQueryParam() },
};

/**
 * レガシーページには Vue Router を積まないため、CartIcon.vue が使う
 * <router-link to="..."> を実ページ遷移可能な <a href> へフォールバックする。
 * SPAコンテキストではVue Routerのbase（Config.spaRoot = '/s/'）が自動付与
 * するパスを、ここでも同様に付与する（付与しないと存在しない
 * /application/cart への404リンクになる）。
 */
export const RouterLinkStub = {
  name: 'RouterLinkStub',
  props: {
    to: { type: String, default: '' },
  },
  render(h) {
    return h('a', { attrs: { href: `/s${this.to}` } }, this.$slots.default);
  },
};
